import { useLoggedViewer } from "@/api/viewer/use-viewer";
import useViewerRole from "@/lib/rights/use-viewer-role";
import useGreeting from "@/utils/get-greeting";
import useTranslation from "@babackapp/translations/use-translation";
import { DateRangePicker } from "@babackapp/ui/components/date-range-picker";
import { useCallback, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import QuickStatDiscountCard from "./components/quick-stat-discount-card";
import QuickStatExchangeCard from "./components/quick-stat-exchange-card";
import QuickStatExchangeCreditCard from "./components/quick-stat-exchange-credit-card";
import QuickStatRefundCard from "./components/quick-stat-refund-card";
import { ReturnRequestReasonsList } from "./components/return-reason-list";
import { ReturnRequestProductsList } from "./components/return-request-products-list";
import { SizingDiagnosticList } from "./components/sizing-diagnostic-list";
import { WavingHandEmoji } from "./components/waving-hand-emoji";
import {
  DashboardFiltersProvider,
  INITIAL_DASHBOARD_FILTERS,
} from "./lib/dashboard-filters";
import { AlertCircle, RefreshCcw, ShoppingBag } from "lucide-react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useAnalyticsProductsList from "@babackapp/private-api-client/client/use-analytics-products-list";
import { DashboardFacetedFilter } from "./components/dashboard-faceted-filter";
import { useReasonText } from "./hooks/use-reason-text";
import { ReturnRequestResolutionPieChart } from "@/pages/(app)/home/components/return-request-resolution-pie-chart.tsx";
import { ReturnRequestEvolutionChart } from "@/pages/(app)/home/components/return-request-evolution-chart.tsx";

export default function Home() {
  const t = useTranslation();
  const { data: user } = useLoggedViewer();
  const name = user?.profile?.firstName ?? "";
  const greeting = useGreeting();
  const viewerRole = useViewerRole();

  const [filters, setFilters] = useState(INITIAL_DASHBOARD_FILTERS);
  const { data: shop } = useCurrentShopSuspense();
  const { data: products } = useAnalyticsProductsList({ shopId: shop?.id });

  const selectedProduct = useMemo(
    () => products?.find((p) => p.id === filters.productId) ?? null,
    [products, filters.productId],
  );

  const selectedVariant = useMemo(
    () =>
      selectedProduct?.variants?.find(
        (v) => v.variantId === filters.variantId,
      ) ?? null,
    [selectedProduct, filters.variantId],
  );

  const reasonText = useReasonText(filters.reason);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const setProductFilter = useCallback(
    (productId: string | undefined, variantId: string | undefined) => {
      scrollToTop();
      setFilters((old) => ({
        ...old,
        productId,
        variantId,
      }));
    },
    [scrollToTop],
  );

  const setResolutionFilter = useCallback(
    (resolution: string | undefined) => {
      scrollToTop();
      setFilters((old) => ({
        ...old,
        resolution,
      }));
    },
    [scrollToTop],
  );

  const setReasonFilter = useCallback(
    (reason: string | undefined) => {
      scrollToTop();
      setFilters((old) => ({
        ...old,
        reason,
      }));
    },
    [scrollToTop],
  );

  if (viewerRole === "logistician") {
    return <Navigate to={`/shops/${shop.id}/returns`} replace />;
  }

  const renderActiveFilters = () => (
    <div className="flex flex-wrap items-center gap-2">
      {selectedProduct && (
        <DashboardFacetedFilter
          label={
            selectedVariant
              ? `${selectedProduct.title} - ${selectedVariant.title}`
              : selectedProduct.title
          }
          icon={<ShoppingBag className="h-3.5 w-3.5" />}
          onClear={() => setProductFilter(undefined, undefined)}
        />
      )}

      {filters.resolution && (
        <DashboardFacetedFilter
          label={filters.resolution}
          icon={<RefreshCcw className="h-3.5 w-3.5" />}
          onClear={() => setResolutionFilter(undefined)}
        />
      )}

      {filters.reason && (
        <DashboardFacetedFilter
          label={reasonText}
          icon={<AlertCircle className="h-3.5 w-3.5" />}
          onClear={() => setReasonFilter(undefined)}
        />
      )}
    </div>
  );

  return (
    <DashboardFiltersProvider value={filters}>
      <div className="flex items-center justify-center">
        <div className="grid grid-cols-4 gap-4 w-full max-w-[1600px] lg:px-16 lg:py-12 px-6 py-6">
          <div className="col-span-4 mb-4 space-y-4">
            <h3 className="text-3xl leading-6 font-semibold text-gray-900">
              {greeting},{" "}
              <span className="font-extrabold text-primary italic">{name}</span>{" "}
              <WavingHandEmoji />
            </h3>
            <p className="text-sm leading-relaxed text-muted-foreground max-w-[500px]">
              {t("analyticsDashboard.subtitle")}
            </p>
          </div>

          <div className="col-span-4 flex items-start gap-2 justify-between">
            <div className="flex-grow overflow-hidden">
              {renderActiveFilters()}
            </div>

            <div className="flex-shrink-0 ml-2">
              <DateRangePicker
                range={{
                  from: filters.fromTimestamp ?? 0,
                  to: filters.toTimestamp,
                }}
                onSelect={(range) => {
                  if (range.from && range.to) {
                    setFilters((old) => ({
                      ...old,
                      fromTimestamp: range.from,
                      toTimestamp: range.to,
                    }));
                  }
                }}
              />
            </div>
          </div>

          {/* Quick stats directly in the grid */}
          <QuickStatDiscountCard />
          <QuickStatExchangeCard />
          <QuickStatRefundCard />
          <QuickStatExchangeCreditCard />

          <ReturnRequestResolutionPieChart
            setResolutionFilter={setResolutionFilter}
          />

          {/* Second row: Return rate gauge + Evolution chart
          <ReturnRequestReturnRatePieChart
            setResolutionFilter={setResolutionFilter}
          />
          <TreatmentSunburstChart />
          <TreatmentStorytellingFlow />
          <TreatmentBigNumbers />
          <ReturnTreatmentEvolutionChart />
          */}
          {/* Products and Reasons lists */}
          <ReturnRequestEvolutionChart />
          <ReturnRequestProductsList setProductFilter={setProductFilter} />
          <ReturnRequestReasonsList setReasonFilter={setReasonFilter} />
          <SizingDiagnosticList />
        </div>
      </div>
    </DashboardFiltersProvider>
  );
}
